/**
 * A long-form itinerary article — a route told stop by stop.
 *
 * Sibling of `DiscoverArticle.tsx`: same "render a node list through a local `renderNode`"
 * shape, same reused primitives from `components/discover/discoverSections`. The prose comes
 * from the resolved `ArticleData` document (`BlogArticleRoute` picks this renderer for
 * `category === "Itineraries"`); the route furniture is stitched on from the `exploreGuide`
 * query:
 *
 *  - a section is a route stop when it carries a `geo` (or its heading reads "Days 1 to 5: …");
 *  - `exploreGuide(geo: <country>)` gives the stop coordinates for the hero / sidebar route
 *    map (`TripPreviewMap`) and each destination's `kind` (City / Island / National park / …),
 *    resolved through the shared `kindConfig` to drive the filter chips and per-stop meta line;
 *  - `exploreGuide(geo: <country>/<stop>)` gives that stop's `stays.hotels`, rendered through
 *    the shared `TravelWidget`;
 *  - "N nights" is read off the section heading ("Days 1 to 5" → 5), never counted from the
 *    body, so a dropped day never renumbers the rest.
 *
 * The hero stat chips are still derived placeholders (highest `Day N`, stop count) — a proper
 * data source for those is a later pass.
 */

import { gql, useQuery } from "@apollo/client";
import type { HotelPreview } from "components/b2c/explore/TravelWidget";
import TravelWidget from "components/b2c/explore/TravelWidget";
import { countryToAlpha2 } from "components/b2c/shared/countryUtils";
import type { ArticleData, ArticleNode, ArticleSection } from "components/discover/articleData";
import { renderBlock } from "components/discover/blockRegistry";
import type { DestinationTypeFilter, GuideIconName, ItineraryHeroStat, ItineraryNavStop } from "components/discover/discoverSections";
import {
	ArticleEmbed,
	ArticleList,
	ArticlePhoto,
	ArticleTable,
	DestinationTypeFilters,
	Faq,
	GuideIcon,
	GuideLangContext,
	GuideUiContext,
	ItineraryHero,
	ItinerarySidebar,
	Para,
	ReadingProgressBar,
	RelatedArticles,
	RichText,
	WaveDownloadCTA,
	kindConfig,
	useScrollSpy,
} from "components/discover/discoverSections";
import type { TripPreviewMapDestination } from "components/map/TripPreviewMap";
import trackEvent from "components/tracking/trackEvent";
import { bylineOf } from "components/website/authorByline";
import DownloadAppQrCard from "components/website/components/DownloadAppQrCard";
import Footer from "components/website/components/Footer";
import InlineInstallCard from "components/website/components/InlineInstallCard";
import LanguageSwitcher from "components/website/components/LanguageSwitcher";
import Navigation from "components/website/components/navigation/Navigation";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { getBlogTranslations } from "components/website/marketing/i18n/pages/blog";
import { t } from "i18n/index";
import { buildLocalizedPath, isMarketingLangCode } from "i18n/urlLocale";
import { useEffect, useMemo, useState } from "react";
import { articleTripHref, withRoutePreviewLang, withUtmContent } from "./articleTripHref";
import styles from "./ItineraryArticle.module.scss";

/** `t()` has no placeholder support — fill `{name}` style tokens after the lookup. */
const fill = (str: string, vars: Record<string, string>) => str.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);

/** Sidebar accent for a stop whose destination kind we could not resolve. Matches "Whole route". */
const DEFAULT_ACCENT = "#13c892";

/** A section that maps to a route stop: the exporter tags it with a `geo`, and its heading
 *  reads "Day 3" / "Days 1 to 5: …". The `geo` is authoritative; the heading is the fallback
 *  for the few stops the exporter leaves ungeo'd. */
const STOP_HEADING_RE = /^\s*days?\b/i;
const isStopSection = (s: ArticleSection) => Boolean(s.geo) || STOP_HEADING_RE.test(s.heading);

/** "Days 1 to 5: Tokyo, and a day in Hakone" → { eyebrow: "Days 1 to 5", place: "Tokyo, …" }.
 *  Only splits when the left side is a day label, so a plain section heading is never cut. */
function splitHeading(heading: string): { eyebrow?: string; place: string } {
	const m = heading.match(/^\s*(days?\b[^:–—-]*?)\s*[:–—-]\s*(.+)$/i);
	return m ? { eyebrow: m[1].trim(), place: m[2].trim() } : { place: heading.trim() };
}

/** Nights at a base, from the section heading only. "Days 1 to 5" → 5, "Day 5" → 1, anything
 *  else → undefined (no chip). Inclusive of both endpoints, which is how the articles count
 *  ("Five nights in Tokyo" for Days 1 to 5) and what makes the per-stop nights sum to the
 *  route total in the quick-reference box. */
function nightsFromHeading(heading: string): number | undefined {
	const range = heading.match(/days?\s+(\d+)\s*(?:to|through|–|—|-)\s*(\d+)/i);
	if (range) return Math.max(1, Number(range[2]) - Number(range[1]) + 1);
	if (/\bday\s+\d+\b/i.test(heading)) return 1;
	return undefined;
}

/** The highest `Day N` written anywhere in the body — the trip length, without counting or
 *  renumbering anything. Ranges ("Days 12 to 14") and per-day headings both contribute. */
function maxDayNumber(article: ArticleData): number {
	let max = 0;
	const scan = (text?: string | null) => {
		if (!text) return;
		for (const m of text.matchAll(/\d+/g)) max = Math.max(max, Number(m[0]));
	};
	for (const section of article.sections) {
		if (isStopSection(section)) scan(section.heading);
		for (const node of section.content) if (node.kind === "heading") scan(node.label);
	}
	return max;
}

// ── Node renderer — one entry per `ArticleNode` kind, same shape as DiscoverArticle ─────────
function renderNode(node: ArticleNode, key: number, article: ArticleData) {
	if (node.kind === "para") return <Para key={key}>{node.text}</Para>;
	if (node.kind === "image") {
		// The caption credits; the alt describes — folding them into one string printed the
		// same sentence twice. Dropping the credit would breach the Unsplash / Pexels terms.
		const caption = node.credit
			? fill(t(getBlogTranslations(article.locale), "blog.article.photoCredit", "Photo by {name} via {source}"), {
					name: node.credit.name,
					source: node.credit.source,
				})
			: undefined;
		return <ArticlePhoto key={key} src={node.url} alt={node.alt} caption={caption} />;
	}
	if (node.kind === "table") return <ArticleTable key={key} headers={node.headers} rows={node.rows} />;
	if (node.kind === "list") return <ArticleList key={key} items={node.items} ordered={node.ordered} />;
	if (node.kind === "embed") return <ArticleEmbed key={key} src={node.src} height={node.height} title={node.title} />;
	if (node.kind === "heading")
		return (
			<h3 key={key} id={node.id} className={styles.introHeading}>
				{node.label && <span className={styles.introHeadingLabel}>{node.label}</span>}
				<RichText text={node.text} />
			</h3>
		);
	// An unknown node kind — a content sync landed ahead of the deploy that teaches this page
	// the kind. Degrade to prose so the words stay on the page rather than disappearing.
	if (!(node as { type?: string }).type) {
		const unknown = node as { text?: unknown; items?: unknown };
		const text =
			typeof unknown.text === "string"
				? unknown.text
				: Array.isArray(unknown.items)
					? unknown.items.filter((i): i is string => typeof i === "string").join(", ")
					: null;
		if (process.env.NODE_ENV !== "production") {
			console.warn("[Itinerary] unknown node kind, rendered as prose:", (node as { kind?: string }).kind);
		}
		return text ? <Para key={key}>{text}</Para> : null;
	}
	return renderBlock(node, key, { slug: article.slug, category: article.category, geo: article.geo, locale: article.locale });
}

/** Which of the three related-article surfaces a reader actually uses is its own question. */
const trackRelated = (slug: string, placement: "sidebar" | "footer") => trackEvent("blog_related_clicked", { slug, placement }, "blog");

interface ItineraryArticleProps {
	/** locale -> slug for this article's translations, from the knowledge base. */
	localeSlugs?: Record<string, string>;
	/**
	 * The resolved article. Fetching is `BlogArticleRoute`'s job, not this component's: the
	 * route has to be able to fall through to the Strapi renderer when a slug is not a
	 * knowledge-base article, and a component that returns null on a miss cannot do that.
	 */
	article: ArticleData;
}

// ── Page ───────────────────────────────────────────────────────────────────────────────────
const ItineraryArticle = ({ article, localeSlugs }: ItineraryArticleProps) => {
	// One view per article, not per render — mirrors DiscoverArticle so both renderers report
	// the same event on the same URL space.
	useEffect(() => {
		trackEvent("blog_article_viewed", { slug: article.slug, geo: article.geo ?? undefined, category: article.category, locale: article.locale }, "blog");
	}, [article.slug, article.geo, article.category, article.locale]);

	// The article's own locale drives the chrome strings — it is the language of the resolved
	// content a reader on `/nl/blog/<slug>` is looking at.
	const tr = getBlogTranslations(article.locale);
	// Same source for related-card hrefs: `article.locale` is correct during SSR, unlike
	// `window.location.pathname`. It's a plain `string` off the knowledge-base document, so
	// narrow it before handing it to `buildLocalizedPath` (typed against `MARKETING_LANG_CODES`).
	const articleLang = isMarketingLangCode(article.locale) ? article.locale : "en";

	// The sidebar's map link and CTA open the trip the article itself links (by trip id, never the
	// old name-based `/stippl_/trip/<name>` form — see `articleTripHref`). `utm_content` tells the
	// two entry points apart on the trip page. An article with no such link keeps the CTA as a
	// plan CTA (signup modal, store on a phone — same as `RouteCtaBlock`) and drops the map link.
	// The route preview renders in the article's language through `?lang=` (`withRoutePreviewLang`), like `RouteCtaBlock`.
	const tripHref = useMemo(() => {
		const href = articleTripHref(article);
		return href && withRoutePreviewLang(href, articleLang);
	}, [article, articleLang]);
	const sidebarMapHref = tripHref ? withUtmContent(tripHref, "sidebar_map") : undefined;
	const sidebarCtaHref = tripHref ? withUtmContent(tripHref, "sidebar_cta") : undefined;
	const planGate = usePlanGate("blog_sidebar_cta", "store");

	const heroPhoto = article.hero?.url ?? "";
	// `article.hero.alt`, the KB's reader-facing description of `heroPhoto` — used below on the
	// wave CTA's `<img>`, the only place this page renders that photo outside `ItineraryHero`'s
	// `aria-hidden` decoration (123yb6937nz). Falls back to the article title.
	const heroPhotoAlt = article.hero?.alt || article.title;
	const lead = article.intro.find((n): n is Extract<ArticleNode, { kind: "para" }> => n.kind === "para");
	const restIntro = article.intro.filter((n) => n !== lead);
	const countryLabel = article.breadcrumbs.find((c) => c.url?.startsWith("/discover/"))?.name;
	const countrySlug = article.countrySlug || article.geo?.split("/")[0] || "";
	const flagCode = (countryLabel && countryToAlpha2.get(countryLabel))?.toLowerCase();
	const crumbs = article.breadcrumbs.length
		? article.breadcrumbs.map((c) => c.name)
		: [t(tr, "blog.article.crumbBlog", "Blog"), article.category, article.title];
	const authorMeta = [
		`${article.readingMinutes} ${t(tr, "blog.minRead", "min read")}`,
		article.lastUpdated ? `${t(tr, "blog.article.updated", "Updated")} ${article.lastUpdated}` : null,
	]
		.filter(Boolean)
		.join(" · ");

	// The route slugs, in visiting order, from `article.stops` — used to filter and order the
	// country guide's marker feed.
	const routeOrder = useMemo(() => new Map((article.stops ?? []).map((s, i) => [s.slug, s.order ?? i])), [article.stops]);

	// One model per section, in document order. `geoSlug` resolves the stop's destination even
	// when the exporter left `section.geo` off, by matching a route slug in the heading.
	const sections = useMemo(
		() =>
			article.sections.map((section) => {
				const stop = isStopSection(section);
				const split = stop ? splitHeading(section.heading) : { place: section.heading };
				const firstImage = section.content.find((n): n is Extract<ArticleNode, { kind: "image" }> => n.kind === "image");
				const geoSlug =
					section.geo?.split("/").pop() ||
					(stop ? (article.stops ?? []).find((st) => new RegExp(`\\b${st.slug.replace(/-/g, "[ -]")}\\b`, "i").test(section.heading))?.slug : undefined);
				return {
					id: section.id,
					isStop: stop,
					eyebrow: stop ? split.eyebrow : undefined,
					place: split.place,
					geoSlug,
					geo: geoSlug && countrySlug ? `${countrySlug}/${geoSlug}` : undefined,
					nights: stop ? nightsFromHeading(section.heading) : undefined,
					fallbackThumb: firstImage?.url || heroPhoto,
					content: section.content,
				};
			}),
		[article.sections, article.stops, countrySlug, heroPhoto]
	);
	const stopSections = useMemo(() => sections.filter((s) => s.isStop), [sections]);
	const stopGeos = useMemo(() => Array.from(new Set(stopSections.map((s) => s.geo).filter((g): g is string => !!g))), [stopSections]);

	// One request: the country guide (markers + destination categories) plus one aliased
	// `exploreGuide` per stop for its `stays.hotels`. Each geo is a query variable, never
	// interpolated into the document string.
	const guidesQuery = useMemo(() => {
		const params = stopGeos.map((_, i) => `$g${i}: String!`).join(", ");
		const fields = stopGeos.map((_, i) => `s${i}: exploreGuide(geo: $g${i}, language: $lang)`).join("\n\t\t\t");
		return gql(`
			query ItineraryGuides($country: String!, $lang: String${params ? ", " + params : ""}) {
				country: exploreGuide(geo: $country, language: $lang)
				${fields}
			}
		`);
	}, [stopGeos]);
	const guidesVars = useMemo(() => {
		const v: Record<string, string> = { country: article.geo ?? "", lang: article.locale };
		stopGeos.forEach((g, i) => (v[`g${i}`] = g));
		return v;
	}, [stopGeos, article.geo, article.locale]);
	const { data: guides } = useQuery(guidesQuery, { variables: guidesVars, skip: !article.geo, fetchPolicy: "cache-first" });

	const country = guides?.country ?? null;
	const stopGuideByGeo = useMemo(() => {
		const m = new Map<string, any>();
		stopGeos.forEach((g, i) => guides?.[`s${i}`] && m.set(g, guides[`s${i}`]));
		return m;
	}, [guides, stopGeos]);

	// slug → { kind, photo } from the country guide's destination list. `kind` is the
	// per-destination type ("Island", "National park", "Heritage site", …), not the group
	// bucket the guide page tabs on.
	const destIndex = useMemo(() => {
		const m = new Map<string, { kind?: string; photo?: string }>();
		for (const g of country?.destinations ?? []) {
			for (const it of g.items ?? []) if (it.slug) m.set(it.slug, { kind: it.kind, photo: it.photo });
		}
		return m;
	}, [country]);

	// slug → map marker from the country guide's marker feed. Gives each stop a clean
	// destination name ("Pai", not the section heading "Days 7 to 10: Pai, the mountain
	// loop") and the geo centre the stay widget needs to seed the in-app stay search.
	const markerBySlug = useMemo(() => {
		const m = new Map<string, { name: string; lat: number; lng: number }>();
		const all: Array<{ slug: string; name: string; lat: number; lng: number }> = Array.isArray(country?.mapMarkers) ? country.mapMarkers : [];
		for (const mk of all) if (mk.slug && Number.isFinite(mk.lat) && Number.isFinite(mk.lng)) m.set(mk.slug, { name: mk.name, lat: mk.lat, lng: mk.lng });
		return m;
	}, [country]);

	// Per stop section: its kind chip (colour + icon + label from the shared `kindConfig`)
	// plus its hotel list, resolved once the guides land.
	const stopMeta = useMemo(() => {
		const m = new Map<
			string,
			{
				kindKey?: string;
				kindLabel?: string;
				color: string;
				icon?: GuideIconName;
				thumb: string;
				hotels: HotelPreview[];
				placeName?: string;
				lat?: number;
				lng?: number;
			}
		>();
		for (const s of stopSections) {
			const dest = s.geoSlug ? destIndex.get(s.geoSlug) : undefined;
			const cfg = dest?.kind ? kindConfig(dest.kind) : undefined;
			const stays = s.geo ? stopGuideByGeo.get(s.geo)?.stays : undefined;
			const marker = s.geoSlug ? markerBySlug.get(s.geoSlug) : undefined;
			m.set(s.id, {
				kindKey: cfg?.key,
				kindLabel: cfg?.label,
				color: cfg?.color || DEFAULT_ACCENT,
				icon: cfg?.icon,
				thumb: dest?.photo || s.fallbackThumb,
				hotels: Array.isArray(stays?.hotels) ? (stays.hotels as HotelPreview[]) : [],
				// The marker name when we have one, else the bare place from the heading: drop a
				// leading "Days 7 to 10:" / "Dag 7 tot 10:" day label (keyed on the colon so it
				// works in every language) and any ", the mountain loop" tail.
				placeName: marker?.name || (s.place.includes(":") ? s.place.slice(s.place.indexOf(":") + 1) : s.place).split(",")[0].trim(),
				lat: marker?.lat,
				lng: marker?.lng,
			});
		}
		return m;
	}, [stopSections, destIndex, stopGuideByGeo, markerBySlug]);

	// Filter chips — the destination kinds actually on this route, each with a count.
	const [theme, setTheme] = useState<string>("all");
	const typeFilters: DestinationTypeFilter[] = useMemo(() => {
		const order: string[] = [];
		const seen = new Map<string, { label: string; color: string; icon?: GuideIconName }>();
		for (const s of stopSections) {
			const meta = stopMeta.get(s.id);
			if (!meta?.kindKey) continue;
			if (!seen.has(meta.kindKey)) {
				seen.set(meta.kindKey, { label: meta.kindLabel || meta.kindKey, color: meta.color, icon: meta.icon });
				order.push(meta.kindKey);
			}
		}
		if (order.length < 2) return [];
		return [
			{ key: "all", label: t(tr, "blog.itinerary.wholeRoute", "Whole route"), color: DEFAULT_ACCENT, count: stopSections.length },
			...order.map((key) => {
				const cfg = seen.get(key)!;
				return {
					key,
					label: cfg.label,
					color: cfg.color,
					icon: cfg.icon,
					count: stopSections.filter((s) => stopMeta.get(s.id)?.kindKey === key).length,
				};
			}),
		];
	}, [stopSections, stopMeta, tr]);
	const hasFilter = typeFilters.length > 0;
	const isVisible = (s: (typeof sections)[number]) => !s.isStop || !hasFilter || theme === "all" || stopMeta.get(s.id)?.kindKey === theme;

	const visibleStopIds = useMemo(() => stopSections.filter(isVisible).map((s) => s.id), [stopSections, hasFilter, theme, stopMeta]);
	const activeId = useScrollSpy(visibleStopIds);

	const dayCount = useMemo(() => maxDayNumber(article), [article]);
	const heroStats: ItineraryHeroStat[] = (
		[
			dayCount ? { value: String(dayCount), label: t(tr, "blog.itinerary.statDays", "days"), icon: "calendar" } : null,
			article.stops?.length ? { value: String(article.stops.length), label: t(tr, "blog.itinerary.statStops", "stops"), icon: "destinations" } : null,
		] as (ItineraryHeroStat | null)[]
	).filter((s): s is ItineraryHeroStat => s !== null);

	// Route stops for the hero / sidebar `TripPreviewMap`, filtered to this route and put in
	// visiting order so the map draws the line stop 0 → stop N.
	const routeDestinations: TripPreviewMapDestination[] = useMemo(() => {
		const all: Array<{ slug: string; name: string; lat: number; lng: number }> = Array.isArray(country?.mapMarkers) ? country.mapMarkers : [];
		return all
			.filter((m) => routeOrder.has(m.slug) && Number.isFinite(m.lat) && Number.isFinite(m.lng))
			.sort((a, b) => (routeOrder.get(a.slug) ?? 0) - (routeOrder.get(b.slug) ?? 0))
			.map((m) => ({ _id: m.slug, cityEnglish: m.name, latitude: m.lat, longitude: m.lng }));
	}, [country, routeOrder]);

	const navStops: ItineraryNavStop[] = stopSections.filter(isVisible).map((s) => ({
		id: s.id,
		place: s.place,
		days: s.eyebrow ?? "",
		thumb: stopMeta.get(s.id)?.thumb || s.fallbackThumb,
		accentColor: stopMeta.get(s.id)?.color ?? DEFAULT_ACCENT,
	}));

	const nightsLabel = (n: number) => (n === 1 ? `1 ${t(tr, "blog.itinerary.night", "night")}` : `${n} ${t(tr, "blog.itinerary.nights", "nights")}`);

	const renderSection = (s: (typeof sections)[number], index: number) => {
		const meta = s.isStop ? stopMeta.get(s.id) : undefined;
		const showMeta = !!(meta?.kindLabel || s.nights != null);
		return (
			<section key={s.id} id={s.id} className={styles.stop}>
				{s.eyebrow && <span className={styles.stopEyebrow}>{s.eyebrow}</span>}
				<h2 className={styles.stopTitle}>{s.place}</h2>
				{showMeta && (
					<div className={styles.stopMeta}>
						{meta?.kindLabel && (
							<span className={styles.stopTheme} style={{ color: meta.color }}>
								{meta.icon && <GuideIcon name={meta.icon} fill="#0f3e4a" size={14} />}
								{meta.kindLabel}
							</span>
						)}
						{meta?.kindLabel && s.nights != null && <span className={styles.stopMetaDot} aria-hidden />}
						{s.nights != null && <span className={styles.stopNights}>{nightsLabel(s.nights)}</span>}
					</div>
				)}
				{s.content.map((node, i) => renderNode(node, index * 1000 + i, article))}
				{meta && meta.hotels.length > 0 && (
					<div className={styles.stayWidget}>
						<TravelWidget
							variant="hotels"
							destination={meta.placeName || s.place}
							country={countryLabel}
							lat={meta.lat}
							lng={meta.lng}
							title={fill(t(tr, "blog.itinerary.stayTitle", "Our favourite places to stay in {place}"), { place: meta.placeName || s.place })}
							hotels={meta.hotels}
						/>
					</div>
				)}
			</section>
		);
	};

	return (
		<GuideUiContext.Provider value={tr}>
			{/* Same as DiscoverArticle: `RichText`'s in-body links (route-preview links included,
			    as `?lang=`) take the article's locale. Missing here since this renderer split off. */}
			<GuideLangContext.Provider value={articleLang}>
				<div id="stippl-itinerary-article" className={styles.page}>
					<ReadingProgressBar />
					<Navigation impact={true} langMenu={<LanguageSwitcher type="article" localeSlugs={localeSlugs} />} />

					<ItineraryHero
						flagCode={flagCode}
						crumbs={crumbs}
						title={article.title}
						lead={lead?.text ?? ""}
						author={bylineOf(article.author)}
						authorMeta={authorMeta}
						stats={heroStats}
						mapId={article.slug}
						destinations={routeDestinations}
						mapBadgeLabel={countryLabel && dayCount ? `${countryLabel} · ${dayCount} ${t(tr, "blog.itinerary.statDays", "days")}` : countryLabel}
						heroPhoto={heroPhoto}
						shareTitle={article.title}
						shareLabels={{
							share: t(tr, "blog.article.share", "Share"),
							copyLink: t(tr, "blog.article.copyLink", "Copy link"),
							shareOn: t(tr, "blog.article.shareOn", "Share on"),
						}}
					/>

					<div className={styles.contentWrap}>
						<div className={styles.contentGrid}>
							<article className={styles.article}>
								{restIntro.map((node, i) => renderNode(node, i, article))}
								{hasFilter && <DestinationTypeFilters filters={typeFilters} active={theme} onChange={setTheme} />}
								{/* work/install-card-mobile.md's A3: after the intro (and the filter chips, when
							    present), before the reader is into the stop-by-stop route. Mobile/tablet only
							    (CSS-hidden on desktop, see InlineInstallCard.scss). Lands here rather than
							    between individual stops because `sections` below is filtered/scroll-spied as
							    one list feeding the sidebar's `activeId` — splitting it would risk that
							    behaviour for no placement benefit. `countryLabel` is undefined on articles
							    with no resolved country — the card falls back to a country-agnostic title. */}
								<InlineInstallCard country={countryLabel} locale={article.locale} />
								<div className={styles.stops}>{sections.filter(isVisible).map((s, i) => renderSection(s, i))}</div>
								{article.faq.length > 0 && (
									<Faq
										guide={{
											name: countryLabel || article.title,
											faqs: article.faq.map((f) => ({ q: f.question, a: f.answer })),
										}}
										kicker={t(tr, "blog.article.faqKicker", "Everything else")}
										title={fill(t(tr, "blog.article.faqTitle", "Frequently asked questions about {name}"), { name: countryLabel || article.title })}
									/>
								)}
								{(article.outro || []).length > 0 && <div>{article.outro!.map((node, i) => renderNode(node, 900000 + i, article))}</div>}
							</article>

							<ItinerarySidebar
								stops={navStops}
								activeId={activeId}
								flagCode={flagCode}
								mapId={article.slug}
								destinations={routeDestinations}
								mapBadgeLabel={countryLabel}
								mapHref={sidebarMapHref}
								routeLabel={t(tr, "blog.itinerary.theRoute", "The route")}
								mapLinkLabel={t(tr, "blog.itinerary.viewRouteOnMap", "View route on the map")}
								cta={{
									title: t(tr, "blog.itinerary.sidebarCtaTitle", "Plan this route yourself?"),
									body: t(
										tr,
										"blog.itinerary.sidebarCtaBody",
										"Open the full route in Stippl: every stop on the map, with trains, hotels and bookings in one app."
									),
									label: t(tr, "blog.itinerary.sidebarCtaLabel", "Open in Stippl"),
									href: sidebarCtaHref,
									onClick: sidebarCtaHref ? undefined : planGate.requestPlan,
								}}
							/>
						</div>
					</div>
					{/* Only ever opens from the no-trip CTA above; renders nothing while closed. Outside the grid so its overlay is not a grid item. */}
					<PlanGateModal gate={planGate} title={t(tr, "blog.block.routeCta.modalTitle", "Create a free account to build your route")} />

					{heroPhoto && countryLabel && (
						<WaveDownloadCTA
							photo={heroPhoto}
							alt={heroPhotoAlt}
							country={countryLabel}
							title={fill(t(tr, "blog.article.ctaTitle", "Plan {country} in Stippl"), { country: countryLabel })}
							body={fill(
								t(
									tr,
									"blog.article.ctaBodyItinerary",
									"Everything in this itinerary, ready to drop into your {country} route, with planning and bookings in one app."
								),
								{ country: countryLabel }
							)}
							kicker={t(tr, "blog.article.ctaKicker", "Ready when you are")}
							scanLabel={t(tr, "blog.article.scanMe", "Scan me")}
							scanAlt={t(tr, "blog.article.scanAlt", "Scan to download the Stippl app")}
						/>
					)}

					{article.related.length > 0 && (
						<RelatedArticles
							heading={t(tr, "blog.article.relatedHeading", "Discover more adventures")}
							kicker={t(tr, "blog.article.relatedKicker", "Read on")}
							readLabel={t(tr, "blog.article.readSuffix", "read")}
							articles={article.related.map((r) => ({
								onClick: () => trackRelated(r.slug, "footer"),
								title: r.title,
								tag: r.category,
								photo: r.image,
								read: r.readingMinutes ? `${r.readingMinutes} ${t(tr, "blog.article.minAbbr", "min")}` : undefined,
								href: buildLocalizedPath(`/blog/${r.slug}`, articleLang),
							}))}
						/>
					)}

					<Footer />
					{/* The KB layout branch of /blog/:article (bugs/blog-install-card-never-renders.md):
				    BlogArticleRoute picks this component, DiscoverArticle, or the legacy
				    BlogArticle — only BlogArticle rendered this card, so every knowledge-base
				    article never showed it. Renders unconditionally, including on articles that
				    also show WaveDownloadCTA above: the card reveals at ~500px (~5% into a long
				    article) while the wave CTA sits at ~82% depth, so gating it off handed the
				    install affordance to only the readers who finish. Reach beats avoiding the
				    stacked QR — do not re-add a gate. */}
					<DownloadAppQrCard />
				</div>
			</GuideLangContext.Provider>
		</GuideUiContext.Provider>
	);
};

export default ItineraryArticle;
